/**
 * React Query hooks for feed stock.
 * React Query hooks for the corresponding entity.
 */

import { useQuery, useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "common/queryKeys";
import {
  BEGINNING_OF_TIME,
  FeedStock,
  FeedStocks,
  GENERAL_FEED_TYPE,
  GENERAL_FEED_UNITS,
  PURCHASE_CURRENCY,
} from "./feedStockTypes";

// Re-export types and constants for convenience
export {
  BEGINNING_OF_TIME,
  feedStockApi,
  GENERAL_FEED_TYPE,
  GENERAL_FEED_UNITS,
  PURCHASE_CURRENCY,
};
export type { FeedStock, FeedStocks };

class FeedStockAPI {
  baseUrl: string;
  entity: string;

  constructor(entity: string) {
    this.baseUrl = `${import.meta.env.VITE_API_ENDPOINT}/v1/`;
    this.entity = entity;
  }

  async fetchFeedStock(params: URLSearchParams): Promise<FeedStocks> {
    const res = await fetch(`${this.baseUrl}${this.entity}?${params}`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${window.localStorage.getItem("jwt") || ""}`,
        "Content-Type": "application/json",
      },
    });
    return { id: 1, feedStock: await res.json() } as FeedStocks;
  }
}

const feedStockApi = new FeedStockAPI("feedStock");

/**
 * Fetch feed stock for a farm
 */
export function useFeedStock(
  farmGuid: string | undefined,
  timeAfter: string = BEGINNING_OF_TIME
) {
  return useQuery({
    queryKey: [...queryKeys.feedStock.byFarm(farmGuid ?? ""), timeAfter],
    queryFn: async () => {
      if (!farmGuid) {
        throw new Error("Farm guid is required");
      }
      return await feedStockApi.fetchFeedStock(
        new URLSearchParams({ farmGuid, timeAfter })
      );
    },
    enabled: !!farmGuid,
  });
}

/**
 * Helper function to get feed stock by type from array
 */
export function getFeedStockByType(
  feedStocks: FeedStock[] | undefined,
  type: GENERAL_FEED_TYPE
): FeedStock | undefined {
  return feedStocks?.find((fs) => fs.type === type);
}

/**
 * Helper function to get total feed stock amount
 */
export function getTotalFeedStockAmount(
  feedStocks: FeedStock[] | undefined
): number {
  if (!feedStocks) return 0;
  return feedStocks.reduce((total, fs) => total + fs.amount, 0);
}

/**
 * Helper function to reset feed stock query cache
 */
export function resetFeedStockQueryCache(
  queryClient: ReturnType<typeof useQueryClient>
): void {
  queryClient.removeQueries({ queryKey: queryKeys.feedStock.all });
}

/**
 * Hook wrapper for resetFeedStockQueryCache
 */
export function useResetFeedStockQueryCache() {
  const queryClient = useQueryClient();
  return () => resetFeedStockQueryCache(queryClient);
}
